import type React from "react";
import {useCallback, useMemo, useState} from "react";
import {useNavigate} from "react-router";
import {Box} from "@mui/material";
import type {GridRowId} from "@mui/x-data-grid-premium";
import ChevronIcon from "~/icons/svg/ChevronIcon.svg?react";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid";
import {useStores} from "~/stores";
import type {
    BuQualification,
    ComplianceRequirementGridRow,
} from "~/pages/supplier-360/types/Supplier360Types";
import {useComplianceBuAggregatesQuery} from "~/pages/supplier-360/tabs/SupplierCompliance/hooks/useComplianceBuGroupsQuery";
import {
    type ComplianceBuRow,
    getComplianceBuColumns,
} from "~/pages/supplier-360/tabs/SupplierCompliance/components/businessUnitsTable/complianceBuColumns";
import {ComplianceBuRequirementsPanel} from "~/pages/supplier-360/tabs/SupplierCompliance/components/businessUnitsTable/ComplianceBuRequirementsPanel";
import {supplierGridInitialState} from "~/pages/supplier-360/components/supplierGridInitialState";
import {DRAWER_HEADER_HEIGHT_LG} from "~/components/main/drawer/DrawerConstants.ts";

/** The row toggle's icons: an arrow pointing right when closed, down when expanded. */
const ExpandIcon = () => <ChevronIcon width={20} height={20}/>;
const CollapseIcon = () => <ChevronIcon width={20} height={20} style={{transform: 'rotate(90deg)'}}/>;

/** The design's open-row tint (`tr.open` → `--ac-tint-accent-2`); no theme token matches it. */
const AC_TINT_ACCENT_2 = 'rgb(238, 243, 251)';
const EXPANDED_ROW_CLASS = 'bu-row--expanded';

type ComplianceBusinessUnitsTableProps = {
    entityId: number;
    qualifications: BuQualification[];
    loading: boolean;
};

/**
 * The "Business units" lens: one row per `bu_qualification` (§2.7), each with its requirement
 * counts from the records grid grouped by BU (§2.8a), matched on the qualification id. A BU without
 * records keeps its row with empty counts. Rows expand into that BU's requirements.
 */
export const ComplianceBusinessUnitsTable: React.FC<ComplianceBusinessUnitsTableProps> = ({
                                                                                              entityId,
                                                                                              qualifications,
                                                                                              loading,
                                                                                          }) => {
    const {bagStore} = useStores();
    const databagId = bagStore.bagId ?? undefined;
    const {data: groups, isFetching: groupsLoading} = useComplianceBuAggregatesQuery(entityId, databagId);

    const rows = useMemo<ComplianceBuRow[]>(() => {
        const byId = new Map((groups ?? []).filter(group => group.id !== null).map(group => [group.id, group]));
        return qualifications.map(qualification => ({...qualification, aggregate: byId.get(String(qualification.id))}));
    }, [groups, qualifications]);

    const navigate = useNavigate();
    // The record detail page is the `compliance/:recordId` child route.
    const openRecord = useCallback((row: ComplianceRequirementGridRow) => navigate(String(row.id)), [navigate]);
    const columns = useMemo(() => getComplianceBuColumns(), []);
    // Tracked here only to tint the open rows — MUI adds no class of its own to an expanded row.
    const [expandedIds, setExpandedIds] = useState<Set<GridRowId>>(() => new Set());

    return (
        <Box sx={{height: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px - 64px - 68px - 56px)`}}>
            <CustomDataGrid
                layoutKey="supplier-360-compliance-business-units"
                initialState={supplierGridInitialState('business_unit', ['category', 'applicable_requirements', 'evidence_coverage'])}
                rows={rows}
                columns={columns}
                loading={loading || groupsLoading}
                showToolbar
                disableRowSelectionOnClick
                getRowHeight={() => 72}
                slots={{detailPanelExpandIcon: ExpandIcon, detailPanelCollapseIcon: CollapseIcon}}
                detailPanelExpandedRowIds={expandedIds}
                onDetailPanelExpandedRowIdsChange={setExpandedIds}
                getRowClassName={({id}) => expandedIds.has(id) ? EXPANDED_ROW_CLASS : ''}
                sx={{[`& .${EXPANDED_ROW_CLASS}, & .${EXPANDED_ROW_CLASS}:hover`]: {bgcolor: AC_TINT_ACCENT_2}}}
                getDetailPanelHeight={() => 'auto'}
                getDetailPanelContent={({row}) => (
                    <ComplianceBuRequirementsPanel
                        entityId={entityId}
                        databagId={databagId}
                        buQualificationId={row.id}
                        businessUnitName={row.business_unit?.bu_name}
                        onOpen={openRecord}
                    />
                )}
                toolbarProps={{title: `${qualifications.length} Business units`}}
                localeText={{noRowsLabel: 'No business units.'}}
            />
        </Box>
    );
};
